import { useQueryClient } from '@tanstack/react-query';
import { ZfStack } from '@zafire/react';
import type { FunctionComponent } from 'react';
import { useCallback, useState } from 'react';
import { useOutletContext } from 'react-router';
import { useClient } from '../../../app/api';
import { BalanceEntryModalWrapper } from '../../../app/components/wealth';
import type { IModalBalanceEntry } from '../../../app/interfaces/balance-sheet-table';
import { useLiquidityOverview } from '../../api';
import { queryKeys } from '../../api/query-keys';
import type { ILiquidityPlannerOutletContext } from '../../interfaces';
import { LiquidityOverviewBarChart } from './liquidity-overview-bar-chart';
import { LiquidityOverviewTable } from './liquidity-overview-table';

export const LiquidityOverview: FunctionComponent = () => {
    const { partnerUuid } = useOutletContext<ILiquidityPlannerOutletContext>();
    const queryClient = useQueryClient();
    const { data: client } = useClient(partnerUuid);
    const { data } = useLiquidityOverview(partnerUuid);
    const [modalEntry, setModalEntry] = useState<IModalBalanceEntry | undefined>(undefined);

    const handleModalClose = useCallback(() => {
        setModalEntry(undefined);
        queryClient.invalidateQueries({ queryKey: queryKeys.liquidityOverview(partnerUuid) });
        queryClient.invalidateQueries({ queryKey: queryKeys.liquidityCalculatorCalculation(partnerUuid) });
    }, [partnerUuid, queryClient]);

    return (
        <ZfStack direction="column" spacing="medium" style={{ flex: 1, minHeight: 0, width: '100%' }}>
            <div style={{ width: '100%', flexShrink: 1 }}>
                <LiquidityOverviewTable overviewData={data} setModalEntry={setModalEntry} />
                {!!modalEntry && (
                    <BalanceEntryModalWrapper
                        woCategoryId={modalEntry.categoryId}
                        partnerUid={partnerUuid}
                        balanceEntry={modalEntry.balanceEntry}
                        onClose={handleModalClose}
                    />
                )}
            </div>
            <LiquidityOverviewBarChart metrics={data?.metrics} currency={client?.currency ?? 'CHF'} />
        </ZfStack>
    );
};
